<template>

  <v-card>
    <loading
      slot="activator"
      :active.sync="isDeleting"
      :can-cancel="false"
      loader="dots"
      :color="appColor_Progress"
    />

    <!-- Main toolbar -->
    <v-toolbar dense :color="toolbarColor" dark flat>
      
      <span v-if="coverImageUri !== null" class="pl-1">
        <v-img width="50" height="50" :src="coverImageUri"></v-img>
      </span>

      <v-toolbar-title :class="titleClass"><b>{{ ownerName }}</b> - <i>{{ name }}</i></v-toolbar-title>

      <v-spacer />

      <v-toolbar-items>
        <v-btn
          @click="done"
          large
          :color="toolbarColorDark"
          class="elevation-0"
          >
        <span class="subheading font-weight-medium">{{ getExpression('Button', 'Close') }}</span></v-btn>
      </v-toolbar-items>
    </v-toolbar>
    
    <!-- Information -->
    <v-alert
      :value="infoAlert"
      color="info"
      border="top"
      dark
      icon="info"
      transition="scale-transition"
      dismissible
      @input="infoAlert = !infoAlert"
      >
      <span class="text-truncate" v-html="playlistInstructions"></span>
    </v-alert>

    <!-- Filters -->
    <v-toolbar flat dense class="my-2">
      <v-card-text class="px-0 mt-3">
        <v-row no-gutters>

          <!-- Media Type Dropdown -->
          <v-col cols="12" sm="4">
            <v-select
              v-model="mediaType"
              :items="mediaTypes"
              item-value="value"
              item-text="text"
              :label="getExpression('Label', 'ContentType')"
              placeholder="Select type"
              hide-details
              :dense="isSmallDevice"
              :class="isSmallDevice ? 'mr-3' : ''"
              >
            </v-select>
          </v-col>

          <!-- Search Text -->
          <v-col cols="12" sm="8">
            <v-text-field
              v-if="$vuetify.breakpoint.name !== 'xs'" 
              v-model="search"
              append-icon="search"
              :label="getExpression('Label', 'Search')"
              hide-details
              class="mx-3"
              >
            </v-text-field>
          </v-col>
        </v-row>
      </v-card-text>

      <v-toolbar-items>
      
        <!-- Preview Button -->
        <v-btn v-if="isKiosk"
          @click="previewPlaylist"
          :disabled="!isPlaylistBlobs"
          class="elevation-0"
          icon
          >
          <v-icon color="info">format_list_numbered</v-icon>
        </v-btn>

        <!-- Help Button -->
        <v-btn
          @click="infoAlert = !infoAlert"
          :disabled="infoAlert"
          class="elevation-0"
          icon
          >
          <v-icon color="info">help_outline</v-icon>
        </v-btn>

        <!-- Delete Button -->
        <v-btn :small="isSmallDevice" :disabled="disableDelete" @click="remove" class="mt-0 ml-2" :color="appColor_DeleteButton">
          <v-icon color="white">delete</v-icon>
        </v-btn>

        <!-- Add Button -->
        <v-btn :small="isSmallDevice" @click="chooseContentType" :color="toolbarColorDark" dark class="elevation-0 mt-0 ml-2" :width="addButtonWidth">
          <span v-if="!isSmallDevice">{{ getExpression('Button', 'AddContent') }}</span>
          <v-icon v-else>add</v-icon>
        </v-btn>

        <input
          v-show="false"
          type="file"
          ref="audio"
          multiple
          accept="audio/mp3"
          @change="fileChanged('audio')"
        />
        
        <input
          v-show="false"
          type="file"
          ref="image"
          multiple
          @change="fileChanged('image')"
        />

        <input
          v-show="false"
          type="file"
          ref="video"
          multiple
          accept="video/mp4"
          @change="fileChanged('video')"
        />        
      </v-toolbar-items>
    </v-toolbar>

    <!-- Data Table -->
    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="filteredItems"
      :loading="isLoading"
      :search="search"
      :custom-filter="customFilter"
      :no-data-text="noDataText"
      show-select
      :footer-props="{
        'items-per-page-text': getExpression('Label', 'RowsPerPage')
      }"
      class="pa-1"
      ref="input"
      >

      <v-progress-linear slot="progress" :color="appColor_Progress" indeterminate />

      <template v-slot:item.thumbnail="{ item }">
        <router-link :to="currentRoute" style="text-decoration: none">
          <span @click="preview(item)">
            <v-img v-if="getMediaType(item.contentType) !== 'audio'" width="50" height="50" :src="getThumbnailUrl(item)" alt=""></v-img>
            <v-icon v-else large color="blue darken-2">volume_up</v-icon>
          </span>
        </router-link>
      </template>

      <template v-slot:item.contentType="{ item }">
        <span class="text-left pl-0">
          <v-icon v-if="getContentTypeAbbr(item.contentType) === 'image'" large>photo</v-icon>
          <v-icon v-if="getContentTypeAbbr(item.contentType) === 'video'" large>movie</v-icon>
          <v-icon v-if="getContentTypeAbbr(item.contentType) === 'audio'" large>audiotrack</v-icon>
        </span>
      </template>

      <template v-slot:item.title="{ item }">
        <span v-if="item.containerName !== container_Public" class="text-left">
          <span @click="edit(item)" style="white-space: nowrap">
            <span :style="appStyle_HyperLink">{{ item.title }}</span>
            <v-icon small class="ml-2" style="cursor: pointer">edit</v-icon>
          </span>
        </span>
        <span v-else>{{ item.title }}</span>
      </template>

      <template v-slot:item.lastViewDate="{ item }">
        <span class="text-left"><app-date :date="item.lastViewDate"/></span>
      </template>

      <template v-slot:item.kioskFolder="{ item }">
        <span class="text-left">{{ getAmbientBehaviour(item.kioskFolder) }}</span>
      </template>

      <template v-slot:item.createDate="{ item }">
        <span class="text-left"><app-date :date="item.createDate" :format="'MMM-DD-YYYY'"/></span>
      </template>

    </v-data-table>

    <!-- Media Previewer -->
    <v-dialog v-model="previewDialog" max-width="800px" persistent>
      <media-previewer
        v-bind:type="previewType"
        v-bind:url="previewUrl"
        v-bind:title="previewTitle"
        v-on:close="closePreview"
      />
    </v-dialog>

    <!-- Playlist Previewer -->
    <v-dialog v-model="previewAmbientDialog" max-width="800px" persistent>
      <playlist-previewer
        v-bind:previewBlobs="ambientPreviewBlobs"
        v-bind:kioskId="kioskId"
        v-bind:playlistName="name"
        v-on:close="closePreviewAmbient"
      />
    </v-dialog>

    <!-- Content Type Selector -->
    <v-dialog v-model="contentTypeSelectorDialog" :max-width="contentTypeSelectorWidth" persistent>
      <content-type-selector
        v-bind:label="contentTypeSelectorLabel"
        v-bind:toolbarColor="toolbarColor"
        v-bind:toolbarColorDark="toolbarColorDark"
        v-on:select="closeChooseContentType"
        v-on:cancel="contentTypeSelectorDialog = false"
      />
    </v-dialog>

    <!-- Media Selector -->
    <v-dialog v-model="mediaDialog" :persistent="true" width="900px">
      <media-selector
        v-model="mediaSelectorParams"
        :containers="containers"
        :defaultContainer="defaultContainer"
        :containerLabel="containerLabel"
        :containerPlaceholder="containerPlaceholder"
        :ownerName="ownerName"
        :toolbarColor="toolbarColor"
        :toolbarColorDark="toolbarColorDark"
        v-on:cancel="mediaDialog = false"
        v-on:ok="addSelectedMedia"
        style="overflow-y: none;"
      />
    </v-dialog>

  </v-card>
</template>

<script>
import appService from '@/app.service'
import { mapGetters } from 'vuex'
import MediaSelector from '@/components/MediaSelector'
import MediaPreviewer from '@/components/MediaPreviewer'
import PlaylistPreviewer from '@/components/PlaylistPreviewer'
import ContentTypeSelector from '@/components/ContentTypeSelector'
import { AppColor, AppStyle, KioskFolder, Container, Base64Image, ThumbnailUrl, Context, ImageUri, LanguageId } from '@/config.js'
import AppDate from '@/components/App/AppDate'

export default {
  components: {
    MediaSelector,
    MediaPreviewer,
    PlaylistPreviewer,
    ContentTypeSelector,
    AppDate
  },
  name: 'PlaylistDetail',
  props: {
    value: {
      type: Array,
      default: null
    },
    id: {
      type: Number,
      default: -1
    },
    name: {
      type: String,
      default: null
    },
    ownerName: {
      type: String,
      default: null
    },
    kioskId: {
      type: Number,
      default: -1
    },
    defaultContainer: {
      type: String,
      default: null
    },
    isFromDashboard: {
      type: Boolean,
      default: false
    },
    coverImageUri: {
      type: String,
      default: null
    },
    toolbarColor: {
      type: String,
      default: AppColor.Medium
    },
    toolbarColorDark: {
      type: String,
      default: AppColor.Dark
    }
  },
  data() {
    return {
      // colors
      appColor_Progress: AppColor.Progress,
      appColor_DeleteButton: AppColor.DeleteButton,

      // kiosk
      kioskFolder_Ambient: KioskFolder.Ambient,
      kioskFolder_Announcement: KioskFolder.Announcement,

      // containers
      container_Public: Container.Public,

      // uri
      thumbnailUrl_Genie: ThumbnailUrl.Genie,
      imageUri_AudioVideoPlaceholder: ImageUri.AudioVideoPlaceholder,

      // icons
      base64Image_AudioIcon: Base64Image.AudioIcon,

      // contexts
      context_Genie: Context.Genie,

      // styles
      appStyle_HyperLink: AppStyle.HyperLink,

      publicContainer: {
        containerName: Container.Public,
        displayName: 'Public'
      },

      playlistId: 0,
      isLoading: false,
      isSaving: false,
      isDeleting: false,
      mediaDialog: false,

      announcementDialog: false,
      previewDialog: false,
      previewType: null,
      previewTitle: null,
      previewUrl: null,
      infoAlert: false,
      previewAmbientDialog: false,
      contentTypeSelectorDialog: false,

      mediaType: 'all',
      search: '',

      playlistBlobs: [],
      selected: [],
      mediaSelectorParams: {},
      ambientPreviewBlobs: [],

      editedIndex: -1,
      editedMediaItem: {
        mediaItemId: null,
        blobName: null,
        title: null,
        kioskFolder: null,
        contentType: null,
        size: null,
        duration: null,
        previewUrl: null
      }
    }
  },
  computed: {
    ...mapGetters(['profile', 'currentRoute', 'activeKiosk', 'activeResident', 'isAdmin', 'locale']),
    noDataText () {
      if (this.isLoading) return this.getExpression('Alert', 'OneMoment')

      return this.value?.length === 0
        ? this.getExpression('Alert', 'NoMedia')
        : ''
    },
    filteredItems () {
      const self = this
      if (self.mediaType !== 'all') {
        return self.blobs.filter(b => {
          return self.getMediaType(b.contentType) === self.mediaType
        })
      } else {
        return self.blobs
      }
    },
    blobs () {
      return this.value ?? []
    },
    isPlaylistBlobs () {
      return this.blobs.length > 0
    },
    isKiosk () {
      const kioskId = this.kioskId ?? 0
      return kioskId > 0
    },
    container () {
      return this.isFromDashboard
          ? this.isKiosk
            ? this.activeKiosk.container
            : this.activeResident.container
          : this.defaultContainer
    },
    containers () {
        return this.isKiosk
          ?  [{ containerName: this.container, displayName: this.ownerName } ]
          :  [{ containerName: this.container, displayName: this.ownerName }, this.publicContainer ]
    },
    containerLabel () {
      return this.isKiosk
        ? this.getExpression('Label', 'Folder')
        : this.getExpression('Label', 'Library')
    },
    containerPlaceholder () {
      return this.isKiosk
        ? this.getExpression('Label', 'Select folder')
        : this.getExpression('Label', 'Select library')
    }, 
    isMediaTypeSelected () {
      return this.mediaType !== 'all'
    },
    headers () {
      var headers = []
      headers.push({
        value: 'mediaItemId',
        align: ' d-none'
      })

      headers.push({
        value: 'thumbnail',
        align: 'center',
        width: '5%',
        sortable: false,
        class: "pr-0"
      })

      headers.push({
        value: 'contentType',
        align: 'left',
        width: '3%',
        class: 'pr-0'
      })

      headers.push({
        text: this.getExpression('Label', 'Title'),
        value: 'title',
        align: 'left',
        width: '20%',
        class: 'pr-0'
      })

      headers.push({
        text: this.getExpression('Label', 'Description'),
        value: 'description',
        align: 'left',
        width: '32%',
        class: 'pr-0'
      })

      headers.push({
        text: this.isKiosk 
          ? this.getExpression('Label', 'Behaviour')
          : this.getExpression('Label', 'LastViewed'),
        value: this.isKiosk 
          ? 'kioskFolder'
          : 'lastViewDate',
        align: 'left',
        width: '20%',
        class: 'pr-0'
      })

      headers.push({
        text: this.getExpression('Label', 'Added'),
        value: 'createDate',
        align: 'left',
        width: '15%',
        class: 'pr-0'
      })
    
      return headers
    },
    mediaTypes () {
      return [
        {
          value: 'all',
          text: this.getExpression('MenuItem', 'All')
        },
        {
          value: 'audio',
          text: this.getExpression('MenuItem', 'Audio')
        },
        {
          value: 'image',
          text: this.getExpression('MenuItem', 'Image')
        },
        {
          value: 'video',
          text: this.getExpression('MenuItem', 'Video')
        }
      ]
    },
    contentTypeSelectorLabel () {
      const label = this.getExpression('Label', 'Content')
      return this.profile.languageId === LanguageId.English
        ? label
        : label.toLowerCase()
    },
    uploadAudioDisabled () {
      return this.mediaType === 'image' || this.mediaType === 'video'
    },
    uploadImageDisabled () {
      return this.mediaType === 'audio' || this.mediaType === 'video'
    },
    uploadVideoDisabled () {
      return this.mediaType === 'audio' || this.mediaType === 'image'
    },
    isMediaSelected () {
      return this.selected.length > 0
    },
    disableDelete () {
      return this.selected.length === 0
    },
    behaviourHint () {
      if (this.editedMediaItem.kioskFolder === null) return null
      return this.editedMediaItem.kioskFolder === this.kioskFolder_Ambient
        ? this.getDescription('Alert', 'AmbientStandardDescription')
        : this.getDescription('Alert', 'AmbientAnnoucementDescription')
    },
    playlistArticle () {
      return this.isKiosk
        ? 'an'
        : 'a'
    },
    playlistDescription () {
      return this.isKiosk
        ? this.getExpression('Label', 'CommunityPlaylist')
        : this.getExpression('Label', 'MemoryAlbum')
    },
    playlistInstructions () {
      return this.getExpression('Alert', 'PlaylistInstruction')
        .replace('{0}', this.playlistArticle)
        .replace('{1}', this.playlistDescription)
        .replace('{2}', `${this.playlistDescription}s`)
    },
    contentTypeSelectorWidth () {
      return this.profile.languageId === LanguageId.English
        ? '220px'
        : '290px'
    },
    titleClass () {
      return this.coverImageUri !== null
        ? 'pl-3 title'
        : 'title'
    },
    addButtonWidth () {
      return !this.isSmallDevice 
        ? this.profile.languageId === LanguageId.English
          ? '160'
          : '200'
        : ''
    }
  },
  methods: {
    addSelectedMedia ({ selectedMedia }) {
      const self = this

      self.isSaving = true
      selectedMedia.forEach(m => { m.playlistId = self.id })
      appService.addPlaylistBlobs(selectedMedia, self.isKiosk).then(data => {
        if (data.errorSeverity > 0) {
          self.handleError(data)
        } else {
          data.playlistBlobs.forEach(b => {
            b.containerOwner = b.containerName === self.container_Public
              ? 'Public'
              : self.ownerName
            self.playlistBlobs.unshift(b)
          })
        }
        self.isSaving = false
        self.mediaDialog = false
        self.emitValue()
        self.mediaSelectorParams = {
          playlistId: -1,
          kioskId: -1
        }
      }).catch(e => { self.handleCriticalError(e) })
    },

    getModel (playlistId, item, foradd) {
      return {
        playlistId: playlistId,
        mediaItemId: item.mediaItemId,
        blobName: item.blobName,
        containerName: item.containerName,
        containerOwner: item.containerOwner,
        kioskFolder: item.kioskFolder,
        title: item.title,
        description: item.description,
        contentType: item.contentType,
        size: item.size,
        duration: item.duration,
        rotate: item.rotate,
        id: foradd ? 0 : item.id,
        createDate: item.createDate,
        thumbnail: foradd ? '' : item.thumbnail
      }
    },

    edit (item) {
      const self = this

      self.editedIndex = this.value?.indexOf(item)
      self.editedMediaItem = Object.assign({}, item)

      const mediaType = self.getMediaType(item.contentType)
      self.editedMediaItem.previewUrl = mediaType !== 'audio'
        ? item.thumbnail
        : self.imageUri_AudioVideoPlaceholder

      self.$media.edit({
        context: self.context_Genie,
        blob: self.editedMediaItem,
        kioskId: self.isKiosk ? self.kioskId : null,
        container: self.container,
        toolbarColor: self.toolbarColor,
        toolbarColorDark: self.toolbarColorDark,

        onSubmit: (mediaItem) => {
          self.playlistBlobs[self.editedIndex].title = mediaItem.title
          self.playlistBlobs[self.editedIndex].description = mediaItem.description

          if (self.isKiosk) {
            self.playlistBlobs[self.editedIndex].kioskFolder = mediaItem.kioskFolder
            self.playlistBlobs[self.editedIndex].blobName = mediaItem.blobName
          }

          self.emitValue()
        }
      })
    },

    remove () {
      const self = this

      self.$confirm.show({
        title: self.getExpression('FormTitle', 'RemovePlaylistItems'),
        text: self.getExpression('Alert', 'RemovePlaylistItemsWarning').replace('{0}', self.selected.length),

        onConfirm: () => {
          self.isDeleting = true
          const ids = self.selected.map(s => s.id)
          self.deleteBlobs(ids).then((data) => {
            if (data.errorSeverity > 0) {
              self.handleError(data)
            } else {
              data.deletedIds.forEach(id => {
                const deletedItems = self.filteredItems.filter(fi => { return fi.id === id })
                if (deletedItems.length > 0) {
                  const indexDeleted = self.playlistBlobs.indexOf(deletedItems[0])
                  self.playlistBlobs.splice(indexDeleted, 1)
                  self.emitValue()
                }
              })
            }
            self.selected = []
            self.isDeleting = false
          }).catch(e => { self.handleCriticalError(e) })
        }
      })
    },

    deleteBlobs (ids) {
      return new Promise(resolve => {
        const updateByUserId = this.profile.id.replace('auth0|', '')

        if (this.isKiosk) {
          appService.deleteKioskPlaylistBlobsBulk(ids, updateByUserId).then(data => {
            resolve(data)
          })
        } else {
          appService.deletePlaylistBlobsBulk(ids, updateByUserId).then(data => {
            resolve(data)
          })
        }
        this.mediaSelectorParams = {
          playlistId: -1,
          kioskId: -1
        }
      })
    },
    
    done () {
      this.emitValue()
      this.$emit('done')
      this.playlistBlobs = []
    },

    preview (item) {
      const self = this
      appService.getBlobUrl(item.containerName, item.blobName).then(data => {
        const mediaType = this.getMediaType(item.contentType)
        self.previewUrl = data
        self.previewType = mediaType
        self.previewTitle = item.title
        self.previewDialog = true
      }).catch(e => { self.handleCriticalError(e) })
    },

    closePreview () {
      this.previewType = null
      this.previewUrl = null
      this.previewDialog = false
    },
    
    previewPlaylist () {
      const self = this
      appService.getKioskAmbientPlaylist(self.id).then(data =>{
         if (data.errorSeverity > 0) {
          self.handleError(data)
        } else {
          self.ambientPreviewBlobs = data.playlistBlobs
        }
      })
      self.previewAmbientDialog = true
    },

    closePreviewAmbient () {
      this.previewAmbientDialog = false
      this.ambientPreviewBlobs = []
    },

    getThumbnailUrl (item) {
      const mediaType = this.getMediaType(item.contentType)
      if (mediaType !== 'audio') {
        const blobName = item.blobName
        const ext = blobName.split('.').pop()
        const thumnailName = blobName.replace(ext, 'jpg')

        const url = `${this.thumbnailUrl_Genie}/${item.containerName}/${thumnailName}`.replace('#', '%23')
        item.thumbnail = url

        return url
      } else {
        const url = this.base64Image_AudioIcon
        item.thumbnail = url

        return url
      }
    },

    getAmbientBehaviour (kioskFolder) {
      return kioskFolder != null
        ? kioskFolder === this.kioskFolder_Ambient
          ? this.getExpression('Label', 'Standard')
          : kioskFolder === this.kioskFolder_Announcement
            ? this.getExpression('Label', 'Announcement')
            : null
        : null
    },

    chooseContentType () {
      this.contentTypeSelectorDialog = true
    },

    closeChooseContentType (contentType) {
      const self = this
      self.contentTypeSelectorDialog = false

      switch (contentType) {
        case 'audio':
        case 'image':
        case 'video':
          self.addMedia(contentType)
          break
        case 'library':
          self.mediaDialog = true
          setTimeout(() => {
            self.mediaSelectorParams = {
              controlId: self.id,
              selectionType: self.isKiosk
                ? 'kiosk-playlist'
                : 'resident-playlist'
            }
          }, 100)
          break
      }    
    },

    addMedia (contentType) {
      if (this.isNullOrUndefined(contentType)) return
      const self = this

      self.contentTypeSelectorDialog = false

      self.$media.add({
        context: self.context_Genie,
        contentType: contentType,
        container: self.container,
        kioskId: self.isKiosk ? self.kioskId : null,
        playlistId: self.id,
        toolbarColor: self.toolbarColor,
        toolbarColorDark: self.toolbarColorDark,

        onSubmit: (uploadResults) => {
          if (uploadResults.length > 0) {
            uploadResults.forEach(result => {
              if (result.success) {
                var playlistBlobModel = self.getModel(self.id, result.mediaItem, true)
                playlistBlobModel.id = result.mediaItem.playlistBlobId
                self.playlistBlobs.unshift(playlistBlobModel)
                self.emitValue()
              }
            })
          }
        }
      })
    },

    emitValue () {
      const self = this
      this.$emit('input', self.playlistBlobs)
    },

    customFilter (value, search, item) {
      const description = item.description ?? ''
      const behaviour = this.getAmbientBehaviour(item.kioskFolder) ?? ''

      return value != null &&
        search != null &&
        item.title.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
        description.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
        behaviour.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1
    },

    handleError (result) {
      this.$error.show({
        title: result.errorSeverity === 0
          ? this.getExpression('FormTitle', 'ValidationFailed')
          : this.getExpression('FormTitle', 'InternalError'),
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  watch: {
    blobs () {
      const self = this
      self.playlistBlobs = []
      self.blobs.forEach(b => { self.playlistBlobs.push(b) })
    },
    
    id (newValue, oldValue) {
      if (oldValue !== newValue) {
        this.mediaType = 'all'
        this.selected = []
      }
    }
  }
}
</script>
